import { useEffect, useState, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import Lenis from "lenis";
import { 
  Activity, Zap, Waves, Brain, ArrowRight, Check, Phone, MapPin, 
  Menu, X, ChevronRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = ({ scrolled, onBookClick }) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      <nav className={`nav ${scrolled ? 'scrolled' : ''}`} data-testid="main-nav">
        <div className="nav-container">
          <a href="#" className="nav-logo" data-testid="nav-logo">BRENDAN JOY</a>
          <div className="nav-links">
            <a href="#services" className="nav-link" data-testid="nav-services">Services</a>
            <a href="#about" className="nav-link" data-testid="nav-about">About</a>
            <a href="#testimonials" className="nav-link" data-testid="nav-testimonials">Results</a>
            <a href="#pricing" className="nav-link" data-testid="nav-pricing">Pricing</a>
          </div>
          <a href="#booking" className="nav-cta" onClick={onBookClick} data-testid="nav-book-btn">Book Now</a>
          <button className="mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>
      <div className={`mobile-menu ${mobileOpen ? 'open' : ''}`} data-testid="mobile-menu">
        <button 
          style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', background: 'none', border: 'none', cursor: 'pointer', zIndex: 110, padding: '0.5rem' }} 
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-menu-close"
        >
          <X color="white" size={32} />
        </button>
        <a href="#services" onClick={() => setMobileOpen(false)}>Services</a>
        <a href="#about" onClick={() => setMobileOpen(false)}>About</a>
        <a href="#testimonials" onClick={() => setMobileOpen(false)}>Results</a>
        <a href="#pricing" onClick={() => setMobileOpen(false)}>Pricing</a>
        <a href="#booking" onClick={() => setMobileOpen(false)}>Book Now</a>
      </div>
    </>
  );
};

// Hero Section
const HeroSection = ({ onBookClick }) => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0.3]);

  return (
    <section className="hero-section" data-testid="hero-section" aria-label="Welcome to Brendan Joy Therapies">
      <motion.div className="hero-bg" style={{ y }}>
        <img 
          src="https://images.unsplash.com/photo-1755798808377-92f8d4651eab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxzdXJmZXIlMjBjaGVja2luZyUyMHdhdmVzJTIwb2NlYW4lMjBjb250ZW1wbGF0aXZlJTIwbWFufGVufDB8fHx8MTc3MzYzMTMxN3ww&ixlib=rb-4.1.0&q=85" 
          alt="Surfer walking towards the ocean in South West Western Australia - representing the active lifestyle of Brendan Joy Therapies clients" 
          loading="eager"
        />
        <div className="hero-overlay"></div>
      </motion.div>
      <motion.div className="hero-content" style={{ opacity }}>
        <motion.p 
          className="eyebrow"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '1rem' }}
        >
          Men's Bodywork Specialist — South West WA
        </motion.p>
        <motion.h1 
          className="hero-headline"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          data-testid="hero-headline"
        >
          Release The Tension You've Been Carrying
        </motion.h1>
        <motion.p 
          className="hero-subheadline"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          Pain, pressure, and stress don't just live in your head — they're stored in your body. 
          Get real results with therapeutic bodywork designed specifically for men.
        </motion.p>
        <motion.div 
          className="hero-modalities"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          role="list"
          aria-label="Therapy modalities offered"
        >
          <span className="modality-tag" role="listitem">Kinesiology</span>
          <span className="modality-tag" role="listitem">Myofascial Release</span>
          <span className="modality-tag" role="listitem">Remedial Massage</span>
          <span className="modality-tag" role="listitem">Craniosacral Therapy</span>
        </motion.div>
        <motion.div 
          className="hero-cta-group"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <button className="btn-primary" onClick={onBookClick} data-testid="hero-book-btn" aria-label="Book your therapy session now">
            Book Your Session <ArrowRight size={20} aria-hidden="true" />
          </button>
          <a href="#services" className="btn-secondary" data-testid="hero-learn-more" aria-label="Learn more about our services">
            Learn More <ChevronRight size={20} aria-hidden="true" />
          </a>
          <div className="hero-phone">
            <Phone size={20} aria-hidden="true" />
            <a href="tel:0431625922" style={{ color: 'white', textDecoration: 'none' }} data-testid="hero-phone" aria-label="Call Brendan Joy on 0431 625 922">0431 625 922</a>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: <Zap size={24} />,
      title: "Functional Kinesiology",
      description: "More than muscle testing — we trace the connections between your body's systems. Meridians, organs, and nervous system all communicate. I help decode that language to find what's really holding you back.",
      featured: true
    },
    {
      icon: <Waves size={24} />,
      title: "Myofascial Release",
      description: "Deep, sustained pressure that releases tension trapped in your fascia. This isn't a relaxation massage — it's targeted work that creates lasting structural change."
    },
    {
      icon: <Activity size={24} />,
      title: "Remedial Massage",
      description: "Focused treatment for injuries and chronic pain. Whether it's a surfing strain or desk-bound tension, we address the specific areas causing you grief."
    },
    {
      icon: <Brain size={24} />,
      title: "Craniosacral Therapy",
      description: "Gentle, powerful work with your central nervous system. Ideal for stress, anxiety, and recovery — helps your body shift from fight-or-flight to rest-and-repair."
    }
  ];

  return (
    <section id="services" className="section" data-testid="services-section">
      <div className="section-container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="eyebrow">What I Do</p>
          <h2 className="section-title">Therapies That Actually Work</h2>
          <p className="section-subtitle">
            21 years of experience, multiple modalities, one goal: get you out of pain and back to doing what you love.
          </p>
        </motion.div>
        <div className="services-grid">
          {services.map((service, index) => (
            <motion.div 
              key={index}
              className={`service-card ${service.featured ? 'featured' : ''}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              data-testid={`service-card-${index}`}
            >
              <div className="service-icon">{service.icon}</div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="section about-section" data-testid="about-section" aria-labelledby="about-heading">
      <div className="section-container">
        <div className="about-grid">
          <motion.div 
            className="about-image-container"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <img 
              src="https://images.unsplash.com/photo-1659518530794-72f034a7d5d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxzb3V0aCUyMHdlc3QlMjBhdXN0cmFsaWElMjBjb2FzdGxpbmUlMjBkdW5zYm9yb3VnaHxlbnwwfHx8fDE3NzM2MzEzMjB8MA&ixlib=rb-4.1.0&q=85" 
              alt="Beautiful coastline of South West Western Australia near Dunsborough - where Brendan Joy Therapies is located" 
              className="about-image"
              loading="lazy"
            />
            <div className="experience-badge" data-testid="experience-badge" aria-label="21 years of professional experience">
              <div className="experience-number">21</div>
              <div className="experience-text">Years Experience</div>
            </div>
          </motion.div>
          <motion.div 
            className="about-content"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="eyebrow">About Brendan</p>
            <h2 id="about-heading">I Fix Bodies. <br/>That's What I Do.</h2>
            <p>
              I've spent over two decades working with men's bodies — athletes, tradies, desk workers, 
              surfers, and everyone in between. What I've learned is that pain rarely exists in isolation. 
              Your hip problem might be connected to how you hold stress. Your neck tension might be 
              linked to a past injury you've forgotten about.
            </p>
            <p>
              My approach combines multiple modalities because bodies are complex. I don't just treat 
              symptoms — I track down the source. And I explain what I'm doing and why, because 
              understanding your body is part of the healing process.
            </p>
            <ul className="about-highlights" role="list" aria-label="Key qualifications">
              <li className="highlight-item" role="listitem">
                <span className="highlight-icon" aria-hidden="true"><Check size={14} /></span>
                <span>Specialised in men's health & sports injuries</span>
              </li>
              <li className="highlight-item" role="listitem">
                <span className="highlight-icon" aria-hidden="true"><Check size={14} /></span>
                <span>Multiple modalities for whole-body treatment</span>
              </li>
              <li className="highlight-item" role="listitem">
                <span className="highlight-icon" aria-hidden="true"><Check size={14} /></span>
                <span>Based in Dunsborough, serving SW WA</span>
              </li>
              <li className="highlight-item" role="listitem">
                <span className="highlight-icon" aria-hidden="true"><Check size={14} /></span>
                <span>Clear communication — no mystical jargon</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = ({ testimonials }) => {
  // Double the testimonials for seamless loop
  const doubledTestimonials = [...testimonials, ...testimonials];

  return (
    <section id="testimonials" className="section testimonials-section" data-testid="testimonials-section">
      <div className="section-container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', maxWidth: '40rem', margin: '0 auto 3rem' }}
        >
          <p className="eyebrow">Real Results</p>
          <h2 className="section-title">What Clients Say</h2>
        </motion.div>
      </div>
      <div className="testimonials-marquee">
        <div className="testimonials-track">
          {doubledTestimonials.map((testimonial, index) => (
            <div key={index} className="testimonial-card" data-testid={`testimonial-card-${index}`}>
              <div className="testimonial-rating">5/5 Stars</div>
              <p className="testimonial-text">"{testimonial.text}"</p>
              <div className="testimonial-author">
                <div className="testimonial-avatar">{testimonial.name.charAt(0)}</div>
                <div>
                  <div className="testimonial-name">{testimonial.name}</div>
                  <div className="testimonial-location">{testimonial.location}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = ({ pricing, onBookClick }) => {
  return (
    <section id="pricing" className="section" data-testid="pricing-section">
      <div className="section-container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', maxWidth: '40rem', margin: '0 auto 4rem' }}
        >
          <p className="eyebrow">Investment</p>
          <h2 className="section-title">Transparent Pricing</h2>
          <p className="section-subtitle">
            Every session is 90 minutes of focused, undivided attention. First-time clients receive a discount on all packages.
          </p>
        </motion.div>
        <div className="pricing-grid">
          {pricing.map((plan, index) => (
            <motion.div 
              key={plan.id}
              className={`pricing-card ${plan.popular ? 'popular' : ''}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              data-testid={`pricing-card-${plan.id}`}
            >
              {plan.popular && <div className="popular-badge">Best Value</div>}
              <h3 className="pricing-name">{plan.name}</h3>
              <p className="pricing-description">{plan.description}</p>
              <div className="pricing-price">
                <span className="price-original"><span className="price-currency">$</span>{plan.price}</span>
                <span className="price-current"><span className="price-currency">$</span>{plan.first_time_price}</span>
                <div className="first-time-badge">First-Time Price</div>
              </div>
              <ul className="pricing-features">
                {plan.features.map((feature, i) => (
                  <li key={i}>
                    <span className="feature-check"><Check size={12} /></span>
                    {feature}
                  </li>
                ))}
                {plan.savings && (
                  <li>
                    <span className="feature-check"><Check size={12} /></span>
                    <strong>Save ${plan.savings}</strong>
                  </li>
                )}
              </ul>
              <button 
                className={`pricing-cta ${plan.popular ? 'primary' : 'secondary'}`}
                onClick={onBookClick}
                data-testid={`pricing-book-${plan.id}`}
              >
                Book {plan.name}
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Booking Form Section
const BookingSection = ({ onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    concern: '',
    preferred_time: '',
    message: '',
    first_time: true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <section id="booking" className="section booking-section" data-testid="booking-section">
      <div className="section-container">
        <div className="booking-grid">
          <motion.div 
            className="booking-info"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="eyebrow" style={{ color: 'rgba(255,255,255,0.7)' }}>Get Started</p>
            <h2>Ready To Feel Different?</h2>
            <p>
              Take the first step towards getting out of pain. Fill out the form and I'll be in touch 
              within 24 hours to schedule your session.
            </p>
            <div className="contact-details">
              <div className="contact-item">
                <div className="contact-icon"><Phone size={20} /></div>
                <div>
                  <div className="contact-text">0431 625 922</div>
                  <div className="contact-label">Call or text anytime</div>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon"><MapPin size={20} /></div>
                <div>
                  <div className="contact-text">South West WA</div>
                  <div className="contact-label">Dunsborough, Margaret River, Yallingup, Busselton</div>
                </div>
              </div>
            </div>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form className="booking-form" onSubmit={handleSubmit} data-testid="booking-form">
              <h3 className="form-title">Book Your Session</h3>
              <p className="form-subtitle">First-time clients receive $50 off their first session.</p>
              
              <div className="form-group">
                <label className="form-label" htmlFor="name">Your Name *</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name"
                  className="form-input" 
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="form-name"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label" htmlFor="email">Email *</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email"
                  className="form-input" 
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="form-email"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label" htmlFor="phone">Phone Number *</label>
                <input 
                  type="tel" 
                  id="phone" 
                  name="phone"
                  className="form-input" 
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  data-testid="form-phone"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label" htmlFor="concern">Main Concern *</label>
                <select 
                  id="concern" 
                  name="concern"
                  className="form-select"
                  value={formData.concern}
                  onChange={handleChange}
                  required
                  data-testid="form-concern"
                >
                  <option value="">Select your main concern</option>
                  <option value="back_pain">Back Pain</option>
                  <option value="neck_pain">Neck & Shoulder Tension</option>
                  <option value="hip_pain">Hip Pain</option>
                  <option value="sports_injury">Sports Injury</option>
                  <option value="stress">Stress & Tension</option>
                  <option value="chronic_pain">Chronic Pain</option>
                  <option value="general_wellness">General Wellness</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label" htmlFor="preferred_time">Preferred Time</label>
                <select 
                  id="preferred_time" 
                  name="preferred_time"
                  className="form-select"
                  value={formData.preferred_time}
                  onChange={handleChange}
                  data-testid="form-time"
                >
                  <option value="">Select preferred time</option>
                  <option value="morning">Morning (8am - 12pm)</option>
                  <option value="afternoon">Afternoon (12pm - 5pm)</option>
                  <option value="evening">Evening (5pm - 8pm)</option>
                  <option value="flexible">I'm flexible</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label" htmlFor="message">Additional Info</label>
                <textarea 
                  id="message" 
                  name="message"
                  className="form-textarea"
                  placeholder="Tell me about your situation, any injuries, or what you're hoping to achieve..."
                  value={formData.message}
                  onChange={handleChange}
                  data-testid="form-message"
                ></textarea>
              </div>
              
              <div className="form-checkbox-group">
                <input 
                  type="checkbox" 
                  id="first_time" 
                  name="first_time"
                  className="form-checkbox"
                  checked={formData.first_time}
                  onChange={handleChange}
                  data-testid="form-first-time"
                />
                <label className="form-checkbox-label" htmlFor="first_time">
                  This is my first session with Brendan (receive $50 off)
                </label>
              </div>
              
              <button type="submit" className="form-submit" disabled={loading} data-testid="form-submit">
                {loading ? 'Sending...' : 'Send Enquiry'}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Toast Component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="toast-container">
      <motion.div 
        className={`toast toast-${type}`}
        initial={{ x: 100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 100, opacity: 0 }}
        data-testid="toast"
      >
        {type === 'success' ? <Check size={20} color="#10B981" /> : <X size={20} color="#EF4444" />}
        <span>{message}</span>
      </motion.div>
    </div>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="footer" data-testid="footer">
      <div className="footer-content">
        <div className="footer-logo">BRENDAN JOY THERAPIES</div>
        <div className="footer-links">
          <a href="#services" className="footer-link">Services</a>
          <a href="#about" className="footer-link">About</a>
          <a href="#pricing" className="footer-link">Pricing</a>
          <a href="#booking" className="footer-link">Contact</a>
        </div>
        <div className="footer-copy">© 2024 Brendan Joy Therapies. South West WA.</div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  const [scrolled, setScrolled] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const lenisRef = useRef(null);

  // Initialize Lenis smooth scroll
  useEffect(() => {
    lenisRef.current = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });

    function raf(time) {
      lenisRef.current?.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => {
      lenisRef.current?.destroy();
    };
  }, []);

  // Handle scroll for nav
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed testimonials first
        await axios.post(`${API}/seed-testimonials`);
        
        // Then fetch all data
        const [testimonialsRes, pricingRes] = await Promise.all([
          axios.get(`${API}/testimonials`),
          axios.get(`${API}/pricing`)
        ]);
        setTestimonials(testimonialsRes.data);
        setPricing(pricingRes.data);
      } catch (e) {
        console.error('Error fetching data:', e);
      }
    };
    fetchData();
  }, []);

  const handleBookClick = () => {
    lenisRef.current?.scrollTo('#booking');
  };

  const handleBookingSubmit = async (formData) => {
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, formData);
      setToast({ message: "Thanks! I'll be in touch within 24 hours.", type: 'success' });
    } catch (e) {
      setToast({ message: 'Something went wrong. Please try calling instead.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <Navigation scrolled={scrolled} onBookClick={handleBookClick} />
      <HeroSection onBookClick={handleBookClick} />
      <ServicesSection />
      <AboutSection />
      {testimonials.length > 0 && <TestimonialsSection testimonials={testimonials} />}
      {pricing.length > 0 && <PricingSection pricing={pricing} onBookClick={handleBookClick} />}
      <BookingSection onSubmit={handleBookingSubmit} loading={loading} />
      <Footer />
      <AnimatePresence>
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </AnimatePresence>
    </div>
  );
}

export default App;
